import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { GMB } from "../../services/gmbApiConfig";

// fetches all Store accounts (accounts under which all the stores are present)
export const fetchAllStoreAccounts = createAsyncThunk(
  "gmbProfile/fetchAllStoreAccounts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.STORE_MANAGEMENT.GET_ALL_ACCOUNTS}`,
        // {},
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );
      if (response.data?.status === "error") {
        toast.error("Failed to fetch store accounts");
        return rejectWithValue(response.data.error_message);
      }
      return response.data.body;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Updates the open status of the store
export const updateStoreOpenStatus = createAsyncThunk(
  "gmbProfile/updateStoreOpenStatus",
  async ({ account_id, location_id, new_open_status }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.STORE_MANAGEMENT.UPDATE_OPEN_STATUS}`,
        { account_id, location_id, new_open_status },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );

      if(response.status!==200){
        toast.error("Please contact Admin")
        return rejectWithValue(response.data.error_message)
      }

      if (response.data?.status === "error") {
        toast.error("Failed to update store status");
        return rejectWithValue(response.data.error_message);
      }
      toast.success("Store Open Status updated successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Updates the labels of the store
export const updateStoreLabels = createAsyncThunk(
  "gmbProfile/updateStoreLabels",
  async ({ account_id, location_id, new_labels }, { rejectWithValue }) => {
    try {
      const labelsArray = new_labels.split(",").map((label) => label.trim());
      const response = await axios.post(
        `${GMB.ENDPOINTS.STORE_MANAGEMENT.UPDATE_LABELS}`,
        { account_id, location_id, new_labels: labelsArray },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );

      if(response.status!==200){
        toast.error("Please contact Admin")
        return rejectWithValue(response.data.error_message)
      }

      if (response.data?.status === "error") {
        toast.error("Failed to update store labels");
        return rejectWithValue(response.data.error_message);
      }

      toast.success("Store Labels updated successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

//updates primary phone number
export const updateStorePhoneNumber = createAsyncThunk(
  "gmbProfile/updateStorePhoneNumber",
  async (
    { account_id, location_id, new_phone_number },
    { rejectWithValue },
  ) => {
    try {
      const cleanedNumber = new_phone_number
        .replace(/[^0-9]/g, "")
        .replace(/^0/, "");
      const response = await axios.post(
        `${GMB.ENDPOINTS.STORE_MANAGEMENT.UPDATE_PHONE_NUMBER}`,
        { account_id, location_id, new_phone_number: cleanedNumber },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );

      if(response.status!==200){
        toast.error("Please contact Admin")
        return rejectWithValue(response.data.error_message)
      }

      if (response.data?.status === "error") {
        toast.error("Failed to update phone number");
        return rejectWithValue(response.data.error_message);
      }

      toast.success("Store PhoneNumber updated successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Transfers the location/ store account to another Store account
export const transferLocation = createAsyncThunk(
  "gmbProfile/transferLocation",
  async ({ new_account_id, location_id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.STORE_MANAGEMENT.TRANSFER_LOC}`,
        { new_account_id, location_id },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );
      if (response.data?.status === "error") {
        toast.error("Failed to transfer location");
        return rejectWithValue(response.data.error_message);
      }

      toast.success("Store is transferred successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

//update the store working time
export const updateStoreWorkingTime = createAsyncThunk(
  "gmbProfile/updateStoreWorkingTime",
  async (
    { account_id, location_id, new_working_time },
    { rejectWithValue },
  ) => {
    try {
      // Parse hours and minutes from new_working_time (e.g., "06:00" to {hours: 6, minutes: 0})
      const [openingHours, openingMinutes] = new_working_time.openingTime
        ?.split(":")
        ?.map(Number);
      const [closingHours, closingMinutes] = new_working_time.closingTime
        ?.split(":")
        ?.map(Number);

      // Construct periods array for each day of the week
      const periods = [
        {
          openDay: "MONDAY",
          openTime: { hours: openingHours, minutes: openingMinutes },
          closeTime: { hours: closingHours, minutes: closingMinutes },
          closeDay: "MONDAY",
        },
        {
          openDay: "TUESDAY",
          openTime: { hours: openingHours, minutes: openingMinutes },
          closeTime: { hours: closingHours, minutes: closingMinutes },
          closeDay: "TUESDAY",
        },
        {
          openDay: "WEDNESDAY",
          openTime: { hours: openingHours, minutes: openingMinutes },
          closeTime: { hours: closingHours, minutes: closingMinutes },
          closeDay: "WEDNESDAY",
        },
        {
          openDay: "THURSDAY",
          openTime: { hours: openingHours, minutes: openingMinutes },
          closeTime: { hours: closingHours, minutes: closingMinutes },
          closeDay: "THURSDAY",
        },
        {
          openDay: "FRIDAY",
          openTime: { hours: openingHours, minutes: openingMinutes },
          closeTime: { hours: closingHours, minutes: closingMinutes },
          closeDay: "FRIDAY",
        },
        {
          openDay: "SATURDAY",
          openTime: { hours: openingHours, minutes: openingMinutes },
          closeTime: { hours: closingHours, minutes: closingMinutes },
          closeDay: "SATURDAY",
        },
        {
          openDay: "SUNDAY",
          openTime: { hours: openingHours, minutes: openingMinutes },
          closeTime: { hours: closingHours, minutes: closingMinutes },
          closeDay: "SUNDAY",
        }, 
      ];

      // Create new_regular_hours object
      const new_regular_hours = { periods };
      const response = await axios.post(
        `${GMB.ENDPOINTS.STORE_MANAGEMENT.UPDATE_REGULAR_HOURS}`,
        { account_id, location_id, new_regular_hours },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );
      
      if(response.status!==200){
        toast.error("Please contact Admin")
        return rejectWithValue(response.data.error_message)
      }

      if (response.data?.status === "error") {
        toast.error("Failed to update working time");
        return rejectWithValue(response.data.error_message);
      }
      toast.success("Store working hours is updated successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

//update store title
export const updateStoreTitle = createAsyncThunk(
  "gmbProfile/updateStoreTitle",
  async ({ account_id, location_id, new_title }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${GMB.ENDPOINTS.STORE_MANAGEMENT.UPDATE_TITLE}`,
        { account_id, location_id, new_title },
        {
          headers: {
            Authorization: Cookies.get("ssp_sso_token"),
            "content-type": "application/json",
          },
        },
      );

      if(response.status!==200){
        toast.error("Please contact Admin")
        return rejectWithValue(response.data.error_message)
      }

      if (response.data?.status === "error") {
        toast.error("Failed to update store title");
        return rejectWithValue(response.data.error_message);
      }

      toast.success("Store title updated successfully");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Initial state
const initialState = {
  formData: {
    title: "",
    storeStatus: "",
    storeLabels: "",
    phoneNumber: "",
    currentAccountName: "",
    operatingHours: {
      openingTime: "",
      closingTime: "",
    },
  },
  originalData: null,
  allStoreAccounts: [],
  loading: false,
  error: null,
  updateStatus: {
    status: false,
    loading: false,
    error: null,
  },
  hasChanges: false,
};

// Slice
const gmbProfileSlice = createSlice({
  name: "gmbProfile",
  initialState,
  reducers: {
    initializeFormData: (state, action) => {
      const {
        open_status,
        labels,
        primary_phone,
        account_name,
        regular_hours,
      } = action.payload;
      const [openingTime, closingTime] = regular_hours
        ? regular_hours.match(/\d{1,2}:\d{2}/g).map((time) => {
            const [hour, minute] = time.split(":").map(Number);
            return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
          })
        : [
            state.formData?.operatingHours?.openingTime.padStart(5, "0") || "00:00",
            state.formData?.operatingHours?.closingTime.padStart(5, "0") || "00:00",
          ];

      const primaryPhone = primary_phone?.replace(/[^\d]|^0+/g, "") || "";
      const formData = {
        storeStatus: open_status || "",
        storeLabels: labels === "-" ? "" : labels,
        phoneNumber: primaryPhone,
        currentAccountName: account_name || "",
        operatingHours: { openingTime, closingTime },
      };
      state.formData = formData;
      state.originalData = { ...formData };
      state.hasChanges = false;
    },
    updateFormField: (state, action) => {
      const { field, value } = action.payload;
      if (field === "operatingHours") {
        state.formData.operatingHours = value;
      } else {
        state.formData[field] = value;
      }
      state.hasChanges =
        JSON.stringify(state.formData) !== JSON.stringify(state.originalData);
    },
    setHasChanges: (state, action) => {
      state.hasChanges = action.payload;
    },
    resetForm: (state) => {
      state.formData = { ...state.originalData };
      state.hasChanges = false;
      state.error = null;
      state.updateStatus = {
        status: false,
        loading: false,
        error: null,
      };
    },
    clearErrors: (state) => {
      state.error = null;
      state.updateStatus.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAllStoreAccounts
      .addCase(fetchAllStoreAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllStoreAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.allStoreAccounts = action.payload;
      })
      .addCase(fetchAllStoreAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // updateStoreOpenStatus
      .addCase(updateStoreOpenStatus.pending, (state) => {
        state.updateStatus.loading = true;
        state.updateStatus.error = null;
      })
      .addCase(updateStoreOpenStatus.fulfilled, (state) => {
        state.updateStatus.loading = false;
        state.updateStatus.status = true;
        state.originalData.storeStatus = state.formData.storeStatus;
        state.hasChanges = false;
      })
      .addCase(updateStoreOpenStatus.rejected, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.error = action.payload;
      })

      // updateStoreLabels
      .addCase(updateStoreLabels.pending, (state) => {
        state.updateStatus.loading = true;
        state.updateStatus.error = null;
      })
      .addCase(updateStoreLabels.fulfilled, (state) => {
        state.updateStatus.loading = false;
        state.updateStatus.status = true;
        state.originalData.storeLabels = state.formData.storeLabels;
        state.hasChanges = false;
      })
      .addCase(updateStoreLabels.rejected, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.error = action.payload;
      })

      // updateStorePhoneNumber
      .addCase(updateStorePhoneNumber.pending, (state) => {
        state.updateStatus.loading = true;
        state.updateStatus.error = null;
      })
      .addCase(updateStorePhoneNumber.fulfilled, (state) => {
        state.updateStatus.loading = false;
        state.updateStatus.status = true;
        state.originalData.phoneNumber = state.formData.phoneNumber;
        state.hasChanges = false;
      })
      .addCase(updateStorePhoneNumber.rejected, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.error = action.payload;
      })

      // transferLocation
      .addCase(transferLocation.pending, (state) => {
        state.updateStatus.loading = true;
        state.updateStatus.error = null;
      })
      .addCase(transferLocation.fulfilled, (state) => {
        state.updateStatus.loading = false;
        state.updateStatus.status = true;
        state.originalData.currentAccountName =
          state.formData.currentAccountName;
        state.hasChanges = false;
      })
      .addCase(transferLocation.rejected, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.error = action.payload;
      })

      //update store working hour
      .addCase(updateStoreWorkingTime.fulfilled, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.status = true;
        state.originalData.operatingHours = state.formData.operatingHours;
        state.hasChanges = false;
      })
      .addCase(updateStoreWorkingTime.rejected, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.error = action.payload;
      })

      //update store title
      .addCase(updateStoreTitle.fulfilled, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.status = true;
        state.originalData.title = state.formData.title;
        state.hasChanges = false;
      })
      .addCase(updateStoreTitle.rejected, (state, action) => {
        state.updateStatus.loading = false;
        state.updateStatus.error = action.payload;
      });
  },
});

// Actions
export const { initializeFormData, updateFormField, resetForm, clearErrors } =
  gmbProfileSlice.actions;

// Selectors
export const selectGMBProfile = (state) => state.gmbProfile;
export const selectFormData = (state) => state.gmbProfile.formData;
export const selectHasChanges = (state) => state.gmbProfile.hasChanges;
// export const setHasChanges = (state) => state.gmbProfile.setHasChanges;
export const selectIsLoading = (state) => state.gmbProfile.loading;
export const selectUpdateStatus = (state) => state.gmbProfile.updateStatus;
export const selectAllStoreAccounts = (state) =>
  state.gmbProfile.allStoreAccounts;
export const { setHasChanges } = gmbProfileSlice.actions;

export default gmbProfileSlice.reducer;
